import { CvValue } from '@/models'
import ApiClient from '@/services/api-client'
import { CV_VALUE_SHORT_CODE_INDEF } from '@/utils/constants'
import Vue from 'vue'

type CvValuesMapArray = {
  [index: string]: CvValue[];
}

type CvValuesMapMap = {
  [index: string]: Map<string, CvValue>;
}

export class CvValueUtils {
  static cvValues: CvValuesMapArray = {}
  static cvValuesAsMap: CvValuesMapMap = {}
  static pendingRequests: { [type: string]: Promise<void> } = {}

  public static async getCvValuesByType(type: string, excludeShortCode = CV_VALUE_SHORT_CODE_INDEF): Promise<CvValue[]> {
    await this.loadCvValuesByType(type)
    let result = this.cvValues[type]
    if (excludeShortCode) {
      result = result.filter((item: CvValue) => item.shortCode !== excludeShortCode)
    }
    return result
  }

  /**
   * Drops the in-memory cache so the next read re-fetches. Call after mutating controlled
   * vocabulary values. Without a type, the whole cache is cleared.
   */
  public static invalidate(type?: string) {
    if (type) {
      Vue.delete(this.cvValues, type)
      Vue.delete(this.cvValuesAsMap, type)
    } else {
      this.cvValues = {}
      this.cvValuesAsMap = {}
    }
  }

  public static getCvValueName(codeId: string, type: string) {
    if (!this.cvValues[type]) {
      return codeId
    }

    const cvValue = this.cvValuesAsMap[type].get(codeId)
    return cvValue ? cvValue.name : codeId
  }

  public static getCvValue(codeId: string, type: string) {
    if (!this.cvValues[type]) {
      return null
    }

    return this.cvValuesAsMap[type].get(codeId)
  }

  private static async loadCvValuesByType(type: string) {
    if (this.cvValues[type]) {
      return
    }

    if (!this.pendingRequests[type]) {
      this.pendingRequests[type] = (async() => {
        try {
          const response = await ApiClient.getCvValuesByType(type)

          Vue.set(this.cvValues, type, response)

          const cvValuesMap = new Map<string, CvValue>()
          response.forEach(item => cvValuesMap.set(item.codeId, item))
          Vue.set(this.cvValuesAsMap, type, cvValuesMap)
        } finally {
          delete this.pendingRequests[type]
        }
      })()
    }

    await this.pendingRequests[type]
  }
}
